import { Box, Stack } from '@mui/material';

import CountryOfBirthField from '../../components/CountryOfBirthField/CountryOfBirthField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import RadioField from '../../components/RadioField/RadioField';
import ResidentPermitField from '../../components/ResidentPermitField/ResidentPermitField';
import Title from '../../components/Title/Title';
import YearMonthField from '../../components/YearMonthField/YearMonthField';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const OTHER_FIELD_NAMES = ['applicantInDenmarkSinceYear', 'applicantResidentPermit', 'applicantAncestralHomeland'];

// Confirmed from legacy: B/D/H/L skip the valid-date; PERMANENT added as a guess, needs backend confirmation (LP-2850)
const PERMITS_WITHOUT_VALID_DATE = ['B', 'D', 'H', 'L', 'PERMANENT'];

const currentYear = new Date().getFullYear();

const CitizenshipPage = () => {
    useComponentTracker('CitizenshipPage');

    const dispatch = useAppDispatch();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const translate = useTranslation();
    const defaultPagesAction = useDefaultPagesAction();

    const citizenship = useAppSelector(selectFieldValue)('applicantCitizenship');
    const isOther = citizenship === 'OTHER';

    const residentPermit = useAppSelector(selectFieldValue)('applicantResidentPermit');
    const showValidDate = isOther && !PERMITS_WITHOUT_VALID_DATE.includes(residentPermit as string);

    const requiredFields = ['applicantCitizenship'];
    if (isOther) {
        requiredFields.push(...OTHER_FIELD_NAMES);
    }
    if (showValidDate) {
        requiredFields.push('applicantResidentPermitValidDate');
    }
    const validationErrors = useValidation(requiredFields);

    useFieldToggle(isOther, OTHER_FIELD_NAMES);
    useFieldToggle(showValidDate, ['applicantResidentPermitValidDate']);

    useSetBannerType('bannerOne');

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });

            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.citizenship.title')} />
            <RadioField
                name="applicantCitizenship"
                options={[
                    { id: 'DANISH', label: translate('field.citizenship.option.danish') },
                    { id: 'OTHER', label: translate('field.citizenship.option.other') },
                ]}
            />
            {isOther && (
                <Stack spacing={2} sx={{ marginTop: 2 }}>
                    <Box>
                        <YearMonthField
                            name="applicantInDenmarkSinceYear"
                            label={translate('field.applicant-in-denmark-since.label')}
                            yearRange={{ from: 1900, to: currentYear }}
                        />
                    </Box>
                    <Box>
                        <ResidentPermitField
                            name="applicantResidentPermit"
                            label={translate('field.applicant-resident-permit.label')}
                        />
                    </Box>
                    {showValidDate && (
                        <Box>
                            <YearMonthField
                                name="applicantResidentPermitValidDate"
                                label={translate('field.applicant-resident-permit-valid-date.label')}
                                yearRange={{ from: currentYear, to: currentYear + 50 }}
                            />
                        </Box>
                    )}
                    <Box>
                        <CountryOfBirthField
                            name="applicantAncestralHomeland"
                            label={translate('field.applicant-ancestral-homeland.label')}
                        />
                    </Box>
                </Stack>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CitizenshipPage;
